/**
 * Storm motion — the vector NWS already ships inside every convective warning.
 *
 * Three encodings of the same fact, in descending order of reliability:
 *
 *   1. `parameters.eventMotionDescription`
 *        "2026-09-21T23:12:00-00:00...storm...245DEG...38KT...LAT...LON 3452 9213"
 *   2. the `TIME...MOT...LOC` line inside `description`
 *        "TIME...MOT...LOC 2312Z 245DEG 38KT 3452 9213"
 *   3. the plain-language sentence, which is all an SPS or an advisory carries
 *        "a confirmed tornado was located near Little Rock, moving northeast at 35 mph"
 *
 * THE CONVENTION TRAP: `245DEG` in forms 1 and 2 is the meteorological *from*
 * bearing — that storm is travelling *toward* 065. The plain-language form is
 * already a toward-bearing. Both are normalised to toward here, at the edge, so
 * that nothing downstream ever has to remember which is which. Get this wrong
 * and every ETA in the app points backwards while still looking plausible.
 */
const KNOTS_TO_MPH = 1.15078;
export function knotsToMph(kt) {
    return Math.round(kt * KNOTS_TO_MPH * 10) / 10;
}
/** 16-point compass, as it appears in NWS prose. Longest keys first when matching. */
const COMPASS = {
    north: 0,
    'north-northeast': 22.5,
    northeast: 45,
    'east-northeast': 67.5,
    east: 90,
    'east-southeast': 112.5,
    southeast: 135,
    'south-southeast': 157.5,
    south: 180,
    'south-southwest': 202.5,
    southwest: 225,
    'west-southwest': 247.5,
    west: 270,
    'west-northwest': 292.5,
    northwest: 315,
    'north-northwest': 337.5,
};
export function bearingFromCompass(word) {
    const k = word.trim().toLowerCase().replace(/ward$/, '').replace(/\s+/g, '-');
    return k in COMPASS ? COMPASS[k] : null;
}
function firstString(v) {
    if (Array.isArray(v))
        return typeof v[0] === 'string' ? v[0] : undefined;
    if (typeof v === 'string')
        return v;
    return undefined;
}
function norm360(deg) {
    return ((deg % 360) + 360) % 360;
}
/** `from` bearing -> `toward` bearing. */
function toward(fromDeg) {
    return norm360(fromDeg + 180);
}
/**
 * "3452 9213" -> [-92.13, 34.52].
 *
 * NWS ships these as hundredths of a degree with the west longitude sign implicit;
 * products in the eastern hemisphere are out of scope for a CONUS alerting system.
 */
function parseLatLon(raw) {
    if (!raw)
        return null;
    const nums = raw.trim().split(/\s+/).map(Number);
    if (nums.length < 2)
        return null;
    const [rawLat, rawLon] = nums;
    if (!Number.isFinite(rawLat) || !Number.isFinite(rawLon))
        return null;
    const lat = rawLat / 100;
    const lon = -Math.abs(rawLon / 100);
    if (Math.abs(lat) > 90 || Math.abs(lon) > 180)
        return null;
    return [lon, lat];
}
/**
 * "2312Z" against a reference day -> ISO. NWS gives only hour and minute, so the
 * date comes from the alert's own `sent` time. If that lands more than a few hours
 * in the future we crossed midnight backwards; step back a day.
 */
function resolveZuluTime(hhmm, reference) {
    const ref = reference ? new Date(reference) : new Date();
    if (Number.isNaN(ref.getTime()))
        return null;
    if (!hhmm || !/^\d{4}$/.test(hhmm))
        return ref.toISOString();
    const hh = Number(hhmm.slice(0, 2));
    const mm = Number(hhmm.slice(2, 4));
    if (hh > 23 || mm > 59)
        return ref.toISOString();
    const d = new Date(Date.UTC(ref.getUTCFullYear(), ref.getUTCMonth(), ref.getUTCDate(), hh, mm, 0, 0));
    if (d.getTime() - ref.getTime() > 6 * 3_600_000)
        d.setUTCDate(d.getUTCDate() - 1);
    return d.toISOString();
}
/** Shared by forms 1 and 2 — both encode "<deg>DEG <kt>KT" plus an optional position. */
const DEG_KT = /(\d{1,3})\s*DEG\b[\s.]*?(\d{1,3})\s*KT\b/i;
function parseDegKt(text, source, observedAt, origin) {
    const m = DEG_KT.exec(text);
    if (!m)
        return null;
    const fromDeg = Number(m[1]);
    const kt = Number(m[2]);
    if (!Number.isFinite(fromDeg) || !Number.isFinite(kt) || fromDeg > 360)
        return null;
    return {
        bearingDeg: toward(fromDeg),
        speedMph: knotsToMph(kt),
        origin,
        observedAt: observedAt ?? new Date().toISOString(),
        source,
        confidence: 'high',
    };
}
/** Form 1: the `eventMotionDescription` CAP parameter. */
function parseEventMotionDescription(parameters, fallbackTime) {
    const raw = firstString(parameters?.eventMotionDescription);
    if (!raw)
        return null;
    // Leading field is a full ISO timestamp; everything after is "...storm...245DEG...38KT..."
    const iso = /^\s*(\d{4}-\d{2}-\d{2}T[\d:]+(?:[.\d]+)?(?:Z|[+-][\d:]+))/.exec(raw)?.[1] ?? null;
    const observedAt = iso && !Number.isNaN(new Date(iso).getTime()) ? new Date(iso).toISOString() : fallbackTime;
    const origin = parseLatLon(/LAT\.*\s*LON\.*\s*([\d\s]+)/i.exec(raw)?.[1]);
    return parseDegKt(raw, 'nws-parameter', observedAt, origin);
}
/** Form 2: the `TIME...MOT...LOC` line in the product text. */
function parseTimeMotLoc(description, fallbackTime) {
    if (!description)
        return null;
    const line = /TIME\.*\s*MOT\.*\s*LOC\.*\s*(\d{4})?Z?\s*([\s\S]{0,80})/i.exec(description);
    if (!line)
        return null;
    const body = line[2] ?? '';
    const observedAt = resolveZuluTime(line[1], fallbackTime);
    const origin = parseLatLon(/KT\s+([\d]{3,4}\s+[\d]{3,5})/i.exec(body)?.[1]);
    return parseDegKt(body, 'nws-text', observedAt, origin);
}
/**
 * Form 3: "moving northeast at 35 mph". Already a toward-bearing, so no flip.
 * The 16-point compass caps this at +/-11.25 degrees of truth, hence medium confidence.
 */
function parsePlainLanguage(description, fallbackTime) {
    if (!description)
        return null;
    const m = /\bmov(?:ing|ed|es)\s+(north|south|east|west|northeast|northwest|southeast|southwest|north[\s-]?northeast|north[\s-]?northwest|east[\s-]?northeast|east[\s-]?southeast|south[\s-]?southeast|south[\s-]?southwest|west[\s-]?southwest|west[\s-]?northwest)(?:ward)?\s+at\s+(\d{1,3})\s*mph\b/i.exec(description);
    if (!m)
        return null;
    const bearing = bearingFromCompass(m[1]);
    const mph = Number(m[2]);
    if (bearing === null || !Number.isFinite(mph))
        return null;
    return {
        bearingDeg: bearing,
        speedMph: mph,
        origin: null,
        observedAt: fallbackTime ?? new Date().toISOString(),
        source: 'nws-plain',
        confidence: 'medium',
    };
}
/**
 * Best available motion vector, or null. Never returns a partial vector: if the
 * speed or the bearing is missing the whole thing is null, because a half-known
 * vector produces a confidently wrong ETA.
 */
export function parseStormMotion(parameters, description, fallbackTime) {
    return (parseEventMotionDescription(parameters, fallbackTime) ??
        parseTimeMotLoc(description, fallbackTime) ??
        parsePlainLanguage(description, fallbackTime) ??
        null);
}
const COMPASS_WORDS = [
    [0, 'north'],
    [22.5, 'north-northeast'],
    [45, 'northeast'],
    [67.5, 'east-northeast'],
    [90, 'east'],
    [112.5, 'east-southeast'],
    [135, 'southeast'],
    [157.5, 'south-southeast'],
    [180, 'south'],
    [202.5, 'south-southwest'],
    [225, 'southwest'],
    [247.5, 'west-southwest'],
    [270, 'west'],
    [292.5, 'west-northwest'],
    [315, 'northwest'],
    [337.5, 'north-northwest'],
];
/** 47 -> "northeast". The UI says this, never a number of degrees. */
export function compassWord(bearingDeg) {
    const b = norm360(bearingDeg);
    let best = COMPASS_WORDS[0];
    let bestDelta = 360;
    for (const entry of COMPASS_WORDS) {
        const delta = Math.abs(((b - entry[0] + 540) % 360) - 180);
        if (delta < bestDelta) {
            bestDelta = delta;
            best = entry;
        }
    }
    return best[1];
}
//# sourceMappingURL=motion.js.map